// @flow
import * as React from 'react';
import { sprintf } from 'sprintf-js';
import styled, { css } from 'styled-components';
import { isDefined } from 'utils/functional';
import { getRgbaString } from 'utils/style';
import { PrimaryButton } from 'components/Button';
import { FilestackImage, LogoImage } from 'components/Image';
import Box from 'components/Box';
import { BodySmall } from 'components/Text';
import Icon from 'components/Icon';
import Spinner from 'components/Spinner';
import InfoBox from 'components/InfoBox';
import { Error } from 'components/Form/Field';
import DeleteButton from 'components/TrashDeleteButton';
import FilestackPicker from 'containers/FilestackPicker';
import type { ImageData } from 'api/shared';
import { colors, transition, mediaRetina } from '../theme';

const ImageContainer = styled(Box)`
  transition: all ${transition};
  img {
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
    @supports (object-fit: cover) {
      height: 100%;
      width: 100%;
      object-fit: cover;
    }
  }
  ${props =>
    props.$isBannerWithOverlay &&
    `
    * {
      height: 100%;
    }
    img {
      height: auto;
      @supports (object-fit: cover) {
        height: 100%;
        width: 100%;
        object-fit: cover;
      }
    }
  `}
`;

const DropBox = styled(Box)`
  color: ${props =>
    props.$overlayButton && props.$hasImage ? 'white' : colors.waterBlue};
  transition: all ${transition};
  & > * {
    position: relative;
    border: 1px dashed ${colors.silver};
    border-radius: 4px;
    transition: border ${transition};
    @media ${mediaRetina} {
      border-width: 1.5px;
    }
  }
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: ${props => (props.$overlayButton ? 0 : 6)}px;
    opacity: 0;
    background: ${props =>
      props.$overlayButton && props.$hasImage
        ? 'rgba(0,0,0,0.5)'
        : 'transparent'};
    transition: all ${transition};
  }
`;

const OverlayDropContent = styled(Box)`
  transition: all ${transition};
`;

const DropContainer = styled(
  ({ dragActive, hasImage, overlayButton, ...props }) => <Box {...props} />
)`
  cursor: pointer;
  ${props =>
    props.dragActive &&
    css`
      ${DropBox} {
        & > * {
          border-color: ${props.overlayButton && props.hasImage
            ? 'white'
            : colors.niceBlue};
          color: ${props.overlayButton && props.hasImage
            ? 'white'
            : colors.niceBlue};
        }
        &::before {
          opacity: 1;
        }
      }
      ${OverlayDropContent} {
        opacity: 1;
      }
    `}
`;

type Props = {
  image: ?ImageData,
  name: string,
  onChange: (?ImageData) => void,
  previewWidth?: number,
  previewHeight?: number,
  format?: 'logo' | 'banner',
  message?: string,
  altText?: string,
  previewCrop?: boolean,
  previewCropToCircle?: boolean,
  centerButton?: boolean,
  overlayButton?: boolean,
  buttonOnly?: boolean,
  aspectRatio?: number,
  imageBorderRadius?: string,
  hideRemove?: boolean,
  'data-qa-id': string,
  showConfirmationModal?: Function,
  disableCrop?: boolean,
  defaultImage?: React.ElementType,
  recommendedDimensions?: { width: number, height: number },
  label?: string
};

const ImagePicker = ({
  image,
  name,
  onChange,
  previewWidth,
  previewHeight,
  format,
  message = getText('Drag files here or browse'),
  altText,
  previewCrop,
  previewCropToCircle,
  centerButton,
  overlayButton,
  buttonOnly,
  aspectRatio,
  imageBorderRadius,
  hideRemove,
  'data-qa-id': qaId,
  showConfirmationModal,
  disableCrop,
  defaultImage,
  recommendedDimensions,
  label,
  ...props
}: Props) => {
  const isBannerWithOverlay = format === 'banner' && overlayButton;
  const buttonStyles = buttonOnly
    ? {
        flexDirection: 'column',
        width: previewWidth,
        height: previewHeight,
        justifyContent: 'center',
        textAlign: 'center'
      }
    : {};

  const onChangeHandler = (file: ?ImageData) => {
    onChange(file);
  };

  return (
    <>
      <FilestackPicker
        data-qa-id={`${qaId}-select-image`}
        name={name}
        aspectRatio={aspectRatio}
        disableCrop={disableCrop}
        onChange={onChangeHandler}
        getDimensions={!!recommendedDimensions}
        label={label}
      >
        {({ dragActive, onClick, error, loading }) => (
          <>
            {loading ? (
              <Box
                height={100}
                width={previewWidth || 96}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Box width={32}>
                  <Spinner size="small" />
                </Box>
              </Box>
            ) : (
              <>
                {recommendedDimensions &&
                  image &&
                  ((image.width && recommendedDimensions.width > image.width) ||
                    (image.height &&
                      recommendedDimensions.height > image.height)) && (
                    <Box mb={20}>
                      <InfoBox type="warning">
                        <BodySmall>
                          {sprintf(
                            getText(
                              'Your image looks a bit small. Choose an image %(width)s x %(height)s or larger for best results.'
                            ),
                            {
                              width: recommendedDimensions.width,
                              height: recommendedDimensions.height
                            }
                          )}
                        </BodySmall>
                      </InfoBox>
                    </Box>
                  )}

                <Box position="relative">
                  {!isBannerWithOverlay && (
                    <Box
                      position="absolute"
                      left={-12}
                      right={-12}
                      top={buttonOnly ? 0 : -12}
                      bottom={buttonOnly ? 0 : -12}
                      borderRadius="4px"
                      bg={`rgba(${getRgbaString(colors.babyBlue, 0.2)})`}
                      opacity={dragActive ? 1 : 0}
                      transition={`opacity ${transition}`}
                    />
                  )}

                  <DropContainer
                    position="relative"
                    display="inline-flex"
                    flexWrap="wrap"
                    alignItems="center"
                    flexDirection={centerButton && 'column'}
                    width={(format === 'banner' || centerButton) && '100%'}
                    dragActive={dragActive}
                    hasImage={isDefined(image)}
                    overlayButton={overlayButton}
                  >
                    {!buttonOnly && (
                      <Box
                        width={format === 'banner' && '100%'}
                        position="relative"
                        mr={format !== 'banner' && 48}
                      >
                        <Box
                          display="flex"
                          flexWrap="wrap"
                          alignItems="center"
                          width={format === 'banner' && '100%'}
                          flexDirection={centerButton && 'column'}
                        >
                          <Box
                            position="relative"
                            width={format === 'banner' ? '100%' : previewWidth}
                            // bg={isBannerWithOverlay && colors.battleshipGrey}
                          >
                            {format === 'logo' ? (
                              <LogoImage
                                data-qa-id={`${qaId}-uploaded-image`}
                                width={previewWidth}
                                handle={image ? image.handle : undefined}
                                mimetype={image ? image.mimetype : undefined}
                                initial={altText ? altText.slice(0, 1) : 'P'}
                                alt={altText || getText('Placeholder')}
                              />
                            ) : image ? (
                              <ImageContainer
                                height={
                                  isBannerWithOverlay && [80, null, null, 200]
                                }
                                $isBannerWithOverlay={isBannerWithOverlay}
                                opacity={dragActive && 0.5}
                              >
                                <FilestackImage
                                  data-qa-id={`${qaId}-uploaded-image`}
                                  width={previewWidth}
                                  height={
                                    isBannerWithOverlay ? 200 : previewHeight
                                  }
                                  alt={altText || getText('Preview image')}
                                  fit={previewCrop ? 'crop' : 'max'}
                                  round={previewCropToCircle}
                                  data={image}
                                />
                              </ImageContainer>
                            ) : (
                              <Box
                                position="relative"
                                borderRadius={!isBannerWithOverlay && '8px'}
                                bg={
                                  dragActive && isBannerWithOverlay
                                    ? `rgba(${getRgbaString(
                                        colors.babyBlue,
                                        0.2
                                      )})`
                                    : colors.platinum
                                }
                                height={
                                  format === 'banner'
                                    ? previewHeight || [80, null, null, 200]
                                    : previewHeight
                                }
                                width={
                                  format === 'banner' ? '100%' : previewWidth
                                }
                                overflow="hidden"
                              >
                                {!overlayButton &&
                                  (defaultImage ? (
                                    <Box
                                      component={defaultImage}
                                      maxWidth="100%"
                                      height="auto"
                                    />
                                  ) : (
                                    <Box
                                      position="absolute"
                                      top="50%"
                                      left="50%"
                                      transform="translate(-50%, -50%)"
                                      pointerEvents="none"
                                    >
                                      <Icon
                                        name="image"
                                        width={45}
                                        fill={colors.aluminum}
                                      />
                                    </Box>
                                  ))}
                              </Box>
                            )}
                            {!hideRemove && image && (
                              <DeleteButton
                                title={getText('Remove')}
                                data-qa-id={`${qaId}-remove-image`}
                                onClick={() => {
                                  if (showConfirmationModal) {
                                    showConfirmationModal();
                                  } else {
                                    onChange(null);
                                  }
                                }}
                                isBannerWithOverlay={isBannerWithOverlay}
                              />
                            )}
                          </Box>
                        </Box>
                      </Box>
                    )}
                    {overlayButton ? (
                      <>
                        <DropBox
                          position="absolute"
                          top={0}
                          left={0}
                          p={10}
                          width="100%"
                          height="100%"
                          $overlayButton={overlayButton}
                          $hasImage={isDefined(image)}
                        >
                          <Box height="100%">
                            <OverlayDropContent
                              height="100%"
                              width="100%"
                              display="flex"
                              flexDirection="column"
                              alignItems="center"
                              justifyContent="center"
                              p="6px 16px"
                              opacity={image ? 0 : 1}
                            >
                              <Box>
                                <Icon
                                  display="block"
                                  width={23}
                                  height={19}
                                  fill="currentColor"
                                  name="upload"
                                />
                              </Box>
                              <BodySmall my={0} ml="8px">
                                {message}
                              </BodySmall>
                            </OverlayDropContent>
                          </Box>
                        </DropBox>
                        {image && (
                          <Box
                            position="absolute"
                            zIndex="1"
                            bottom="20px"
                            left="50%"
                            transform="translateX(-50%)"
                          >
                            <PrimaryButton
                              size="small"
                              onClick={onClick}
                              {...props}
                            >
                              {getText('Replace')}
                            </PrimaryButton>
                          </Box>
                        )}
                      </>
                    ) : (
                      <Box my={!buttonOnly && 22} mx={-10}>
                        <DropBox
                          position="relative"
                          borderRadius="4px"
                          p={10}
                          {...props}
                        >
                          <Box
                            display="flex"
                            alignItems="center"
                            p="6px 16px"
                            {...buttonStyles}
                          >
                            <Box>
                              <Icon
                                display="block"
                                width={23}
                                height={19}
                                fill="currentColor"
                                name="upload"
                              />
                            </Box>
                            <BodySmall
                              my={0}
                              ml={!buttonOnly && '8px'}
                              mt={buttonOnly && '8px'}
                            >
                              {message}
                            </BodySmall>
                          </Box>
                        </DropBox>
                      </Box>
                    )}
                  </DropContainer>

                  {error && <Error errorMessage={error} />}
                </Box>
              </>
            )}
          </>
        )}
      </FilestackPicker>
    </>
  );
};

export default ImagePicker;
